'use client';

import { useState } from 'react';
import { useRef } from 'react';

import { Avatar, Popover, SecondaryButton } from '@web/ui';

import styles from './WorkspaceMemberHoverCard.module.scss';

type HoverCardUser = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  email?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
};

type WorkspaceMemberHoverCardProps = {
  user: HoverCardUser;
  roleLabel?: string | null;
  children: React.ReactNode;
  canMessage?: boolean;
  closeOnTriggerMouseLeave?: boolean;
  openOnFocus?: boolean;
  onMessage?: (userId: string) => void;
};

function getDisplayName(user: HoverCardUser) {
  return (
    user.display_name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown member'
  );
}

function getAvatarSrc(user: HoverCardUser) {
  return user.avatar_url ?? user.avatar ?? undefined;
}

export function WorkspaceMemberHoverCard({
  user,
  roleLabel,
  children,
  canMessage = true,
  closeOnTriggerMouseLeave = false,
  openOnFocus = true,
  onMessage,
}: WorkspaceMemberHoverCardProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const name = getDisplayName(user);
  const open = Boolean(anchorEl);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeCard = () => {
    clearCloseTimeout();
    setAnchorEl(null);
  };

  const scheduleCloseCard = () => {
    if (!closeOnTriggerMouseLeave) {
      return;
    }

    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null);
      closeTimeoutRef.current = null;
    }, 120);
  };

  return (
    <span className={styles.workspaceMemberHoverCard}>
      <span
        className={styles.workspaceMemberHoverCard__trigger}
        onMouseEnter={(event) => {
          clearCloseTimeout();
          setAnchorEl(event.currentTarget);
        }}
        onMouseLeave={scheduleCloseCard}
        onFocus={(event) => {
          if (!openOnFocus) {
            return;
          }

          setAnchorEl(event.currentTarget);
        }}
      >
        {children}
      </span>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeCard}
        sx={closeOnTriggerMouseLeave ? { pointerEvents: 'none' } : undefined}
        slotProps={
          closeOnTriggerMouseLeave
            ? {
                paper: {
                  sx: { pointerEvents: 'auto' },
                },
              }
            : undefined
        }
      >
        <div
          className={styles.workspaceMemberHoverCard__content}
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={scheduleCloseCard}
        >
          <div className={styles.workspaceMemberHoverCard__identity}>
            <Avatar
              name={name}
              src={getAvatarSrc(user)}
              size="xLarge"
              showTooltip={false}
            />
            <div className={styles.workspaceMemberHoverCard__text}>
              <strong className={styles.workspaceMemberHoverCard__name}>
                {name}
              </strong>
              {user.email ? (
                <span className={styles.workspaceMemberHoverCard__email}>
                  {user.email}
                </span>
              ) : null}
              {roleLabel ? (
                <span className={styles.workspaceMemberHoverCard__role}>
                  {roleLabel}
                </span>
              ) : null}
            </div>
          </div>
          {onMessage && canMessage ? (
            <div className={styles.workspaceMemberHoverCard__actions}>
              <SecondaryButton
                ariaLabel={`Message ${name}`}
                onClick={() => {
                  setAnchorEl(null);
                  onMessage(user.id);
                }}
              >
                Message
              </SecondaryButton>
            </div>
          ) : null}
        </div>
      </Popover>
    </span>
  );
}
